
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { FileEdit } from "lucide-react";
import { DraftsList } from "./DraftsList";
import { DraftCampaign } from "@/hooks/campaign/hooks/useCampaignInit";
import { useEffect } from "react";
import { AllDraftsDialog } from "./AllDraftsDialog";

interface DraftsCardProps {
  drafts: DraftCampaign[];
  loading: boolean;
  onRefresh?: () => void;
}

export const DraftsCard = ({ drafts, loading, onRefresh }: DraftsCardProps) => {
  const { t } = useLanguage();
  const [showAllDrafts, setShowAllDrafts] = useState(false);
  
  // Log the drafts received in props for debugging
  useEffect(() => {
    console.log('DraftsCard: Received drafts prop:', drafts);
    console.log('DraftsCard: Loading state:', loading);
    console.log('DraftsCard: Drafts count:', drafts?.length || 0);
    
    // Check localStorage directly for debugging
    const allKeys = Object.keys(localStorage);
    const draftKeys = allKeys.filter(key => key.includes('campaign_drafts'));
    console.log('DraftsCard: localStorage draft keys:', draftKeys);
    
    // Try to get drafts directly from localStorage
    draftKeys.forEach(key => {
      try {
        const draftsStr = localStorage.getItem(key);
        if (draftsStr) {
          const parsedDrafts = JSON.parse(draftsStr);
          console.log(`DraftsCard: Found drafts in ${key}:`, parsedDrafts);
        }
      } catch (e) {
        console.error(`DraftsCard: Error parsing drafts from ${key}:`, e);
      }
    });
  }, [drafts, loading]);
  
  const handleDraftDeleted = () => {
    if (onRefresh) {
      console.log('DraftsCard: Draft deleted, triggering refresh');
      onRefresh();
    }
  };
  
  return (
    <>
      <Card className="bg-gradient-to-br from-accent/20 via-card to-card border-border shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
        <CardHeader>
          <CardTitle className="flex flex-col items-center text-center whitespace-nowrap">
            <FileEdit className="h-6 w-6 text-accent-foreground mb-2" />
            <span>{t('resumeDraft')}</span>
          </CardTitle>
          <CardDescription className="text-center">
            {t('continuePreviousDraft')}
          </CardDescription>
        </CardHeader>
        <CardContent className="min-h-28">
          <DraftsList 
            drafts={drafts} 
            loading={loading} 
            onDraftDeleted={handleDraftDeleted}
          />
        </CardContent>
        <CardFooter>
          {drafts.length > 0 ? (
            <Button 
              variant="outline" 
              size="lg" 
              className="w-full"
              onClick={() => setShowAllDrafts(true)}
            >
              {t('viewAllDrafts')}
            </Button>
          ) : (
            <Button 
              variant="outline" 
              size="lg"
              className="w-full"
              disabled
            >
              {t('noDraftsFound')}
            </Button>
          )}
        </CardFooter>
      </Card>
      
      <AllDraftsDialog
        open={showAllDrafts}
        onOpenChange={setShowAllDrafts}
        drafts={drafts}
        loading={loading}
        onDraftDeleted={handleDraftDeleted}
      />
    </>
  );
};
